import { Meter } from "@bfc-software/pallet";

import {
  percentOf,
  type TaskProgress,
  type UnitProgress,
} from "../-data/dashboard-model";
import type { DashboardT } from "./dashboard-t";
import styles from "./dashboard.module.css";

/**
 * The shift's overall completion: tasks done, and units picked.
 *
 * Both are live. The two are
 * drawn as peers because that is what the manager reads together - "30% of tasks
 * but 40% of units" says the remaining tasks are the small ones.
 */
export function CompletionHero({
  tasks,
  units,
  t,
}: {
  tasks: TaskProgress;
  units: UnitProgress;
  t: DashboardT;
}) {
  const unitPercent = percentOf(units.picked, units.total);

  return (
    <section className={styles.hero} data-test-role="dashboard-completion">
      <div className={styles.heroTop}>
        <span>{t("dashboard.completion.title").toUpperCase()}</span>
        <span className={styles.heroScope}>
          {t("dashboard.completion.scope")}
        </span>
      </div>
      <div className={styles.heroFigures}>
        <div className={styles.heroFigure}>
          <div>
            <span
              className={styles.heroValue}
              data-test-role="completion-tasks"
            >
              {tasks.percent}%
            </span>{" "}
            <span className={styles.heroUnit}>
              {t("dashboard.completion.ofTasks")}
            </span>
          </div>
          <Meter
            done={tasks.completed}
            total={tasks.total}
            size="wide"
            tone="good"
            onBrand
            label={t("dashboard.completion.tasksDetail", {
              completed: tasks.completed,
              remaining: tasks.remaining,
              total: tasks.total,
            })}
          />
        </div>
        <div className={styles.heroFigure}>
          <div>
            <span
              className={styles.heroValue}
              data-test-role="completion-units"
            >
              {unitPercent}%
            </span>{" "}
            <span className={styles.heroUnit}>
              {t("dashboard.completion.ofUnits")}
            </span>
          </div>
          <Meter
            done={units.picked}
            total={units.total}
            size="wide"
            tone="good"
            onBrand
            label={t("dashboard.completion.unitsDetail", {
              picked: units.picked.toLocaleString(),
              remaining: (units.total - units.picked).toLocaleString(),
              total: units.total.toLocaleString(),
            })}
          />
        </div>
      </div>
    </section>
  );
}
